
import React, { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Search,
  CheckCircle,
  AlertCircle,
  Edit,
  Trash2,
  Loader2,
  BarChart3,
  Filter,
  X,
  Plus,
  Eye,
} from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

// Error View Modal Component
const ErrorViewModal = ({ open, onOpenChange, item }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            Validation Errors - Question #{(item?.originalIndex || 0) + 1}
          </DialogTitle>
          <DialogDescription>
            Review the validation errors for this question below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="p-3 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-sm mb-2">Question Text:</h4>
            <p className="text-sm">{item?.question || "No question provided"}</p>
          </div>

          <div className="space-y-2">
            <h4 className="font-medium text-red-600">Validation Errors:</h4>
            <div className="space-y-2">
              {item?.errors?.map((error, index) => (
                <div key={index} className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded">
                  <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-red-700">{error}</span>
                </div>
              ))}
            </div>
          </div>

          {item?.choices && item.choices.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Available Options:</h4>
              <div className="grid gap-1">
                {item.choices.map((choice, index) => (
                  <div key={index} className="text-sm p-2 bg-blue-50 rounded">
                    Option_{index + 1}: {choice}
                  </div>
                ))}
              </div>
            </div>
          )}

          {item?.answerReferences && item.answerReferences.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Answer References:</h4>
              <div className="text-sm p-2 bg-yellow-50 rounded">
                {item.answerReferences.join(", ")}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ExcelValidationModal = ({
  open,
  onOpenChange,
  validationData,
  selectedQuestionBank,
  onSubmitValidated,
  showToast,
  isSubmitting = false,
}) => {
  const { toast } = useToast();

  // ... keep existing code (pagination and filtering states)
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [accordionValue, setAccordionValue] = useState("");
  const [deleteExcelIndex, setDeleteExcelIndex] = useState("");

  // ... keep existing code (progress tracking states)
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentBatch, setCurrentBatch] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [estimatedTime, setEstimatedTime] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStartTime, setProcessingStartTime] = useState(null);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [newChoice, setNewChoice] = useState("");

  // New state for managing tags input and error view
  const [newTag, setNewTag] = useState("");
  const [errorViewOpen, setErrorViewOpen] = useState(false);
  const [selectedErrorItem, setSelectedErrorItem] = useState(null);

  // State to track validation results with updates
  const [validationResults, setValidationResults] = useState([]);

  // Initialize validation results when validationData changes
  useEffect(() => {
    if (validationData?.validationResults) {
      setValidationResults([...validationData.validationResults]);
    }
  }, [validationData]);

  // ... keep existing code (question bank capacity calculation)
  const questionBankCapacity = useMemo(() => {
    const currentCount = selectedQuestionBank?.questionCount || 0;
    const maxCapacity =
      selectedQuestionBank?.questionLimit ||
      selectedQuestionBank?.maxCapacity ||
      5000;
    const availableSpace = maxCapacity - currentCount;
    const validQuestionsCount =
      validationResults?.filter(
        (item) =>
          item.isValid &&
          !item.errors.some((error) =>
            error.includes("already exists in database")
          )
      ).length || 0;

    return {
      current: currentCount,
      max: maxCapacity,
      available: availableSpace,
      requested: validQuestionsCount,
      hasSpace: availableSpace >= validQuestionsCount,
    };
  }, [selectedQuestionBank, validationResults]);

  // ... keep existing code (filtered validation data, pagination, stats)
  const filteredValidationData = useMemo(() => {
    if (!validationResults) return [];

    const nonExistingQuestions = validationResults.filter(
      (item) =>
        !item.errors.some((error) =>
          error.includes("already exists in database")
        )
    );

    let filtered = nonExistingQuestions;

    if (filterType === "valid") {
      filtered = filtered.filter((item) => item.isValid);
    } else if (filterType === "invalid") {
      filtered = filtered.filter((item) => !item.isValid);
    }

    if (searchTerm) {
      filtered = filtered.filter((item) =>
        item.question.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [validationResults, filterType, searchTerm]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredValidationData.slice(startIndex, endIndex);
  }, [filteredValidationData, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredValidationData.length / pageSize);

  const stats = useMemo(() => {
    if (!validationResults)
      return { total: 0, valid: 0, invalid: 0, existing: 0 };

    const nonExisting = validationResults.filter(
      (item) =>
        !item.errors.some((error) =>
          error.includes("already exists in database")
        )
    );

    const existing = validationResults.filter((item) =>
      item.errors.some((error) => error.includes("already exists in database"))
    ).length;

    const valid = nonExisting.filter((item) => item.isValid).length;
    const invalid = nonExisting.filter((item) => !item.isValid).length;

    return {
      total: nonExisting.length,
      valid,
      invalid,
      existing,
    };
  }, [validationResults]);

  // ... keep existing code (useEffect hooks for pagination reset and progress calculation)
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterType]);

  const calculateProgress = () => {
    if (!isProcessing) return 0;
    const batchProgress = (currentBatch / totalBatches) * 100;
    return Math.min(batchProgress, 100);
  };

  const calculateEstimatedTime = () => {
    if (!processingStartTime || !isProcessing) return 0;
    const elapsedTime = Date.now() - processingStartTime;
    const avgTimePerBatch = elapsedTime / (currentBatch || 1);
    const remainingBatches = totalBatches - currentBatch;
    return Math.ceil((remainingBatches * avgTimePerBatch) / 1000);
  };

  useEffect(() => {
    if (isProcessing) {
      const interval = setInterval(() => {
        setEstimatedTime(calculateEstimatedTime());
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isProcessing, currentBatch, totalBatches, processingStartTime]);

  // ... keep existing code (batch processing logic)
  const processBatches = async (validQuestions) => {
    const BATCH_SIZE = 100;
    const batches = [];

    for (let i = 0; i < validQuestions.length; i += BATCH_SIZE) {
      batches.push(validQuestions.slice(i, i + BATCH_SIZE));
    }

    setTotalBatches(batches.length);
    setIsProcessing(true);
    setProcessingStartTime(Date.now());
    setCurrentBatch(0);

    try {
      for (let i = 0; i < batches.length; i++) {
        setCurrentBatch(i + 1);
        setUploadProgress(((i + 1) / batches.length) * 100);

        // Ensure each batch has properly formatted questions with choices
        const formattedBatch = batches[i].map(question => ({
          ...question,
          choices: question.choices || [],
          answers: question.answers || [],
        }));

        await onSubmitValidated(formattedBatch, true);

        if (i < batches.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }

      toast({
        title: "Success",
        description: `Successfully uploaded ${validQuestions.length} questions in ${batches.length} batches`,
      });

      setTimeout(() => {
        onOpenChange(false);
      }, 1000);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to upload questions. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
      setUploadProgress(0);
      setCurrentBatch(0);
      setTotalBatches(0);
    }
  };


  // Enhanced edit handler with support for multiple answers
  // const handleEdit = (index, item) => {
  //   if (editingIndex === index) {
  //     setEditingIndex(null);
  //     setEditingData({});
  //     setAccordionValue("");
  //   } else {
  //     setEditingIndex(index);
  //     setEditingData({
  //       question: item.question,
  //       answers: Array.isArray(item.answers) ? [...item.answers] : [],
  //       points: item.points,
  //       difficultyLevel: item.difficultyLevel,
  //       tags: Array.isArray(item.tags) ? [...item.tags] : [],
  //     });
  //     setAccordionValue(`edit-${index}`);
  //   }
  //   setNewTag("");
  // };

  // Handle multiple answer selection
  // const handleAnswerChange = (choice, isChecked) => {
  //   const questionType = Array.isArray(selectedQuestionBank?.questionType)
  //     ? selectedQuestionBank.questionType[0]
  //     : selectedQuestionBank?.questionType || "SINGLE_CHOICE";

  //   const currentAnswers = editingData.answers || [];

  //   let newAnswers;
  //   if (questionType === "MULTIPLE_CHOICE") {
  //     if (isChecked) {
  //       newAnswers = [...currentAnswers, choice];
  //     } else {
  //       newAnswers = currentAnswers.filter((answer) => answer !== choice);
  //     }
  //   } else {
  //     newAnswers = isChecked ? [choice] : [];
  //   }

  //   setEditingData({ ...editingData, answers: newAnswers });
  // };

  // Add new tag
  const handleAddTag = () => {
    if (newTag.trim() && !editingData.tags?.includes(newTag.trim())) {
      const currentTags = editingData.tags || [];
      setEditingData({
        ...editingData,
        tags: [...currentTags, newTag.trim()],
      });
      setNewTag("");
    }
  };

  // Remove tag
  const handleRemoveTag = (tagToRemove) => {
    const currentTags = editingData.tags || [];
    setEditingData({
      ...editingData,
      tags: currentTags.filter((tag) => tag !== tagToRemove),
    });
  };

  // Handle Enter key for adding tags
  const handleTagKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  // Frontend validation function
  // const validateQuestion = (questionData) => {
  //   const errors = [];

  //   // Check mandatory fields
  //   if (!questionData.question || !questionData.question.trim()) {
  //     errors.push("Question text is required");
  //   }

  //   if (!questionData.difficultyLevel || !questionData.difficultyLevel.trim()) {
  //     errors.push("Difficulty level is required");
  //   } else {
  //     const validDifficulties = ["easy", "medium", "hard"];
  //     if (!validDifficulties.includes(questionData.difficultyLevel.toLowerCase())) {
  //       errors.push("Difficulty must be 'easy', 'medium', or 'hard'");
  //     }
  //   }

  //   // Check minimum options (at least 3)
  //   if (!questionData.choices || questionData.choices.length < 3) {
  //     errors.push("At least 3 options are required");
  //   }

  //   // Check answers
  //   if (!questionData.answers || questionData.answers.length === 0) {
  //     errors.push("At least one answer is required");
  //   }

  //   return errors;
  // };

  const validateQuestion = (questionData) => {
    const errors = [];

    // Check mandatory fields
    if (!questionData.question || !questionData.question.trim()) {
      errors.push("Question text is required");
    }

    if (!questionData.difficultyLevel || !questionData.difficultyLevel.trim()) {
      errors.push("Difficulty level is required");
    } else {
      const validDifficulties = ["easy", "medium", "hard"];
      if (!validDifficulties.includes(questionData.difficultyLevel.toLowerCase())) {
        errors.push("Difficulty must be 'easy', 'medium', or 'hard'");
      }
    }

    // Check minimum choices (at least 3)
    if (!questionData.choices || questionData.choices.length < 3) {
      errors.push("At least 3 choices are required");
    }

    // Check answers
    if (!questionData.answers || questionData.answers.length === 0) {
      errors.push("At least one answer is required");
    }

    return errors;
  };


  // Updated handleSaveEdit function
  const handleSaveEdit = (index) => {
    const updatedResults = [...validationResults];
    const itemIndex = updatedResults.findIndex(
      (item) => item.originalIndex === index
    );

    if (itemIndex !== -1) {
      // Update the item with new data, ensuring choices are properly included
      const updatedItem = {
        ...updatedResults[itemIndex],
        question: editingData.question || updatedResults[itemIndex].question,
        choices: editingData.choices || updatedResults[itemIndex].choices, // Ensure choices are updated
        answers: editingData.answers || updatedResults[itemIndex].answers,
        points: editingData.points || updatedResults[itemIndex].points,
        difficultyLevel: editingData.difficultyLevel || updatedResults[itemIndex].difficultyLevel,
        tags: editingData.tags || updatedResults[itemIndex].tags,
      };

      // Re-validate the question
      const validationErrors = validateQuestion(updatedItem);
      updatedItem.errors = validationErrors;
      updatedItem.isValid = validationErrors.length === 0;

      updatedResults[itemIndex] = updatedItem;
      setValidationResults(updatedResults);
    }

    setEditingIndex(null);
    setEditingData({});
    setAccordionValue("");
    setNewTag("");

    toast({
      title: "Success",
      description: "Question updated successfully",
    });
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditingData({});
    setAccordionValue("");
    setNewTag("");
  };

  const handleDelete = (index) => {
    const updatedResults = validationResults.filter(
      (item) => item.originalIndex !== deleteExcelIndex
    ).map(item => ({
      ...item,
      originalIndex: item.originalIndex > deleteExcelIndex
        ? item.originalIndex - 1
        : item.originalIndex
    }));

    setValidationResults(updatedResults);
    setShowDeletePopup(false);

    if (editingIndex === deleteExcelIndex) {
      setEditingIndex(null);
      setEditingData({});
      setAccordionValue("");
    }

    toast({
      title: "Success",
      description: "Excel Question deleted successfully",
    });
  };

  // Handle view errors
  const handleViewErrors = (item) => {
    setSelectedErrorItem(item);
    setErrorViewOpen(true);
  };

  // ... keep existing code (handleSubmit, getQuestionStatus, getProgressBarColor)
  const handleSubmit = () => {
    if (!questionBankCapacity.hasSpace) {
      showToast(
        "error",
        "Capacity Exceeded",
        `Question bank capacity exceeded. Available: ${questionBankCapacity.available}, Requested: ${questionBankCapacity.requested}. Please reduce the number of questions or increase the question bank limit.`
      );
      return;
    }

    const validQuestions = validationResults.filter(
      (item) =>
        item.isValid &&
        !item.errors.some((error) =>
          error.includes("already exists in database")
        )
    );

    if (validQuestions.length === 0) {
      showToast(
        "error",
        "No Valid Questions",
        "There are no valid questions to upload."
      );
      return;
    }

    // Ensure all question data including updated choices are properly formatted
    const formattedQuestions = validQuestions.map(question => ({
      ...question,
      choices: question.choices || [], // Ensure choices array exists
      answers: question.answers || [], // Ensure answers array exists
    }));

    if (formattedQuestions.length > 100) {
      processBatches(formattedQuestions);
    } else {
      onSubmitValidated(formattedQuestions);
    }
  };

  const getQuestionStatus = (item) => {
    if (item.isValid) {
      return {
        color: "border-green-500",
        bgColor: "bg-green-50",
        icon: <CheckCircle className="h-4 w-4 text-green-500" />,
        textColor: "text-green-700",
      };
    } else {
      return {
        color: "border-orange-500",
        bgColor: "bg-orange-50",
        icon: <AlertCircle className="h-4 w-4 text-orange-500" />,
        textColor: "text-orange-700",
      };
    }
  };

  const getProgressBarColor = () => {
    if (uploadProgress < 30) return "bg-red-500";
    if (uploadProgress < 70) return "bg-yellow-500";
    return "bg-green-500";
  };

  const hasValidQuestionsToSubmit =
    stats.valid > 0 && questionBankCapacity.hasSpace;

  const handleEdit = (index, item) => {
    if (editingIndex === index) {
      setEditingIndex(null);
      setEditingData({});
      setAccordionValue("");
    } else {
      setEditingIndex(index);
      setEditingData({
        question: item.question,
        choices: Array.isArray(item.choices) ? [...item.choices] : [],
        answers: Array.isArray(item.answers) ? [...item.answers] : [],
        points: item.points,
        difficultyLevel: item.difficultyLevel,
        tags: Array.isArray(item.tags) ? [...item.tags] : [],
      });
      setAccordionValue(`edit-${index}`);
    }
    setNewTag("");
    setNewChoice("");
  };


  const handleAnswerChange = (choice, isChecked) => {
    const questionType = Array.isArray(selectedQuestionBank?.questionType)
      ? selectedQuestionBank.questionType[0]
      : selectedQuestionBank?.questionType || "SINGLE_CHOICE";

    const currentAnswers = editingData.answers || [];

    let newAnswers;
    if (questionType === "MULTIPLE_CHOICE") {
      if (isChecked) {
        newAnswers = [...currentAnswers, choice];
      } else {
        newAnswers = currentAnswers.filter((answer) => answer !== choice);
      }
    } else {
      newAnswers = isChecked ? [choice] : [];
    }

    setEditingData({ ...editingData, answers: newAnswers });
  };

  const handleAddChoice = () => {
    if (newChoice.trim() && !editingData.choices?.includes(newChoice.trim())) {
      const currentChoices = editingData.choices || [];
      setEditingData({
        ...editingData,
        choices: [...currentChoices, newChoice.trim()],
      });
      setNewChoice("");
    }
  };

  // Remove choice
  const handleRemoveChoice = (choiceToRemove) => {
    const currentChoices = editingData.choices || [];
    const currentAnswers = editingData.answers || [];

    // Don't allow removal if it would result in less than 3 choices
    if (currentChoices.length <= 3) {
      toast({
        title: "Cannot Remove",
        description: "A question must have at least 3 choices",
        variant: "destructive",
      });
      return;
    }

    // Remove from choices
    const newChoices = currentChoices.filter((choice) => choice !== choiceToRemove);

    // Remove from answers if it was selected as an answer
    const newAnswers = currentAnswers.filter((answer) => answer !== choiceToRemove);

    setEditingData({
      ...editingData,
      choices: newChoices,
      answers: newAnswers,
    });
  };

  const handleChoiceKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddChoice();
    }
  };


  return (
    <>
      <Dialog open={open} onOpenChange={isProcessing ? () => { } : onOpenChange}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] overflow-hidden flex">
          {/* Sidebar with Stats and Filters */}
          <div className="w-80 border-r bg-gray-50 p-4 flex flex-col">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="h-5 w-5" />
              <h3 className="font-semibold">Statistics</h3>
            </div>

            {/* Capacity Check */}
            <div className="mb-4 p-3 rounded-lg border bg-white">
              <h4 className="font-medium text-sm mb-2">Question Bank Capacity</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Current:</span>
                  <span className="font-medium">
                    {questionBankCapacity.current}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Max Capacity:</span>
                  <span className="font-medium">{questionBankCapacity.max}</span>
                </div>
                <div className="flex justify-between">
                  <span>Available:</span>
                  <span
                    className={`font-medium ${questionBankCapacity.available < 100
                        ? "text-red-600"
                        : "text-green-600"
                      }`}
                  >
                    {questionBankCapacity.available}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Requested:</span>
                  <span
                    className={`font-medium ${!questionBankCapacity.hasSpace
                        ? "text-red-600"
                        : "text-blue-600"
                      }`}
                  >
                    {questionBankCapacity.requested}
                  </span>
                </div>
                {!questionBankCapacity.hasSpace && (
                  <div className="text-red-600 text-xs mt-2 p-2 bg-red-50 rounded">
                    ⚠️ Insufficient capacity! Reduce questions or increase
                    capacity.
                  </div>
                )}
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              <div className="bg-white p-3 rounded-lg border">
                <div className="text-lg font-bold text-blue-600">
                  {stats.total}
                </div>
                <div className="text-xs text-gray-600">Total</div>
              </div>
              <div className="bg-white p-3 rounded-lg border">
                <div className="text-lg font-bold text-green-600">
                  {stats.valid}
                </div>
                <div className="text-xs text-gray-600">Valid</div>
              </div>
              <div className="bg-white p-3 rounded-lg border">
                <div className="text-lg font-bold text-orange-600">
                  {stats.invalid}
                </div>
                <div className="text-xs text-gray-600">Invalid</div>
              </div>
              <div className="bg-white p-3 rounded-lg border">
                <div className="text-lg font-bold text-gray-600">
                  {stats.existing}
                </div>
                <div className="text-xs text-gray-600">Existing</div>
              </div>
            </div>

            {/* Filters */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                <h4 className="font-medium text-sm">Filters</h4>
              </div>

              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Questions</SelectItem>
                  <SelectItem value="valid">Valid Only</SelectItem>
                  <SelectItem value="invalid">Invalid Only</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={pageSize.toString()}
                onValueChange={(value) => setPageSize(parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Items per page" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 per page</SelectItem>
                  <SelectItem value="30">30 per page</SelectItem>
                  <SelectItem value="50">50 per page</SelectItem>
                  <SelectItem value="100">100 per page</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Progress Section */}
            {isProcessing && (
              <div className="mt-4 p-3 bg-white rounded-lg border">
                <h4 className="font-medium text-sm mb-2">Upload Progress</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span>
                      Batch {currentBatch} of {totalBatches}
                    </span>
                    <span>{Math.round(uploadProgress)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${getProgressBarColor()}`}
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-600">
                    Est. time:{" "}
                    {estimatedTime > 0 ? `${estimatedTime}s` : "Calculating..."}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b">
              <DialogHeader>
                <DialogTitle className="text-xl font-semibold">
                  Excel Sheet Validation Results
                </DialogTitle>
                <DialogDescription>
                  Review and edit the questions from your Excel sheet before
                  submitting.
                </DialogDescription>
              </DialogHeader>

              {/* Search and Pagination */}
              <div className="flex gap-4 mt-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search questions..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Pagination Controls */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm px-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>

            {/* Questions List */}
            <div className="flex-1 overflow-auto p-4">
              <Accordion
                type="single"
                value={accordionValue}
                onValueChange={setAccordionValue}
                className="space-y-4"
              >
                {paginatedData.map((item, index) => {
                  const status = getQuestionStatus(item);
                  const isEditing = editingIndex === item.originalIndex;

                  return (
                    <div
                      key={item.originalIndex}
                      className={`border rounded-lg ${status.color} ${status.bgColor}`}
                    >
                      {/* Question Row */}
                      <div className="p-4 flex items-center gap-4">
                        <div className="w-16 font-medium text-center">
                          {item.originalIndex + 1}
                        </div>

                        <div className="flex-1">
                          <div
                            className="font-medium truncate"
                            title={item.question}
                          >
                            {item.question || "No question provided"}
                          </div>
                          {!item.isValid && (
                            <div className="text-xs text-orange-600 mt-1">
                              {item.errors.length} error(s)
                            </div>
                          )}
                        </div>

                        <div className="flex-1">
                          <div className="text-sm">
                            {item.answers && item.answers.length > 0
                              ? item.answers.join(", ")
                              : "No answers"}
                          </div>
                        </div>

                        <div className="w-24 flex items-center justify-center gap-1">
                          {status.icon}
                          <span className={`text-xs ${status.textColor}`}>
                            {item.isValid ? "Valid" : "Invalid"}
                          </span>
                        </div>

                        <div className="w-32 flex items-center justify-center gap-2">
                          {!item.isValid && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewErrors(item)}
                              className="h-8 w-8 p-0 text-blue-500 hover:text-blue-700"
                              disabled={isProcessing}
                              title="View Errors"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant={isEditing ? "default" : "outline"}
                            onClick={() => handleEdit(item.originalIndex, item)}
                            className="h-8 w-8 p-0"
                            disabled={isProcessing}
                            title="Edit Question"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setShowDeletePopup(true); setDeleteExcelIndex(item.originalIndex) }}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                            disabled={isProcessing}
                            title="Delete Question"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Edit Section */}
                      {isEditing && (
                        <AccordionItem
                          value={`edit-${item.originalIndex}`}
                          className="border-none"
                        >
                          <AccordionContent className="px-4 pb-4 bg-white border-t">
                            <div className="mt-4">
                              <div className="flex items-center gap-2 mb-4">
                                <Edit className="h-4 w-4" />
                                <span className="font-medium">
                                  Edit Question #{item.originalIndex + 1}
                                </span>
                              </div>

                              <div className="grid grid-cols-2 gap-6">
                                <div className="space-y-4">
                                  <h4 className="font-semibold text-lg text-gray-800 border-b pb-2">
                                    Answer Selection
                                  </h4>

                                  {/* Manage Choices Section */}
                                  <div className="space-y-3">
                                    <div className="flex items-center gap-2 mb-2">
                                      <span className="text-sm font-medium text-gray-600">Manage Choices:</span>
                                    </div>
                                    <div className="flex gap-2 mb-3">
                                      <Input
                                        value={newChoice}
                                        onChange={(e) => setNewChoice(e.target.value)}
                                        onKeyPress={handleChoiceKeyPress}
                                        placeholder="Add new choice"
                                        className="flex-1"
                                      />
                                      <Button
                                        size="sm"
                                        onClick={handleAddChoice}
                                        disabled={!newChoice.trim()}
                                        className="px-3"
                                      >
                                        <Plus className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>

                                  {/* Answer Selection Section */}
                                  <div className="space-y-3 max-h-60 overflow-y-auto">
                                    {(editingData.choices || []).map((choice, choiceIndex) => {
                                      const questionType = Array.isArray(
                                        selectedQuestionBank?.questionType
                                      )
                                        ? selectedQuestionBank.questionType[0]
                                        : selectedQuestionBank?.questionType ||
                                        "SINGLE_CHOICE";

                                      return (
                                        <div key={choiceIndex} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50">
                                          <input
                                            type={
                                              questionType === "MULTIPLE_CHOICE"
                                                ? "checkbox"
                                                : "radio"
                                            }
                                            name={`answer-${item.originalIndex}`}
                                            value={choice}
                                            checked={
                                              editingData.answers?.includes(
                                                choice
                                              ) || false
                                            }
                                            onChange={(e) =>
                                              handleAnswerChange(
                                                choice,
                                                e.target.checked
                                              )
                                            }
                                            className="w-4 h-4 text-blue-600"
                                          />
                                          <span className="text-sm font-medium text-gray-700 flex-1">
                                            {choice}
                                          </span>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => handleRemoveChoice(choice)}
                                            className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                            disabled={(editingData.choices || []).length <= 3}
                                            title={
                                              (editingData.choices || []).length <= 3
                                                ? "Cannot remove - minimum 3 choices required"
                                                : "Remove choice"
                                            }
                                          >
                                            <X className="h-3 w-3" />
                                          </Button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>

                                {/* <Separator orientation="vertical" className="mx-4" /> */}

                                <div className="space-y-4">
                                  <h4 className="font-semibold text-lg text-gray-800 border-b pb-2">
                                    Question Details
                                  </h4>
                                  <div className="space-y-4 max-h-60 overflow-y-auto">
                                    <div>
                                      <Label className="text-sm font-medium text-gray-700">
                                        Question *
                                      </Label>
                                      <Input
                                        value={
                                          editingData.question || item.question
                                        }
                                        onChange={(e) =>
                                          setEditingData({
                                            ...editingData,
                                            question: e.target.value,
                                          })
                                        }
                                        className="mt-1"
                                        placeholder="Enter question text"
                                      />
                                    </div>

                                    <div>
                                      <Label className="text-sm font-medium text-gray-700">
                                        Points
                                      </Label>
                                      <Input
                                        type="number"
                                        min="1"
                                        value={
                                          editingData.points || item.points || 1
                                        }
                                        onChange={(e) =>
                                          setEditingData({
                                            ...editingData,
                                            points: parseInt(e.target.value) || 1,
                                          })
                                        }
                                        className="mt-1"
                                      />
                                    </div>

                                    <div>
                                      <Label className="text-sm font-medium text-gray-700">
                                        Difficulty *
                                      </Label>
                                      <Select
                                        value={
                                          editingData.difficultyLevel ||
                                          item.difficultyLevel
                                        }
                                        onValueChange={(value) =>
                                          setEditingData({
                                            ...editingData,
                                            difficultyLevel: value,
                                          })
                                        }
                                      >
                                        <SelectTrigger className="mt-1">
                                          <SelectValue placeholder="Select difficulty" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="easy">
                                            Easy
                                          </SelectItem>
                                          <SelectItem value="medium">
                                            Medium
                                          </SelectItem>
                                          <SelectItem value="hard">
                                            Hard
                                          </SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>


                                    <div>
                                      <Label className="text-sm font-medium text-gray-700">
                                        Tags
                                      </Label>
                                      <div className="mt-1 space-y-2">
                                        {/* Display existing tags as chips */}
                                        <div className="flex flex-wrap gap-2">
                                          {(editingData.tags || []).map(
                                            (tag, tagIndex) => (
                                              <Badge
                                                key={tagIndex}
                                                variant="secondary"
                                                className="flex items-center gap-1 px-2 py-1"
                                              >
                                                {tag}
                                                <Button
                                                  size="sm"
                                                  variant="ghost"
                                                  onClick={() =>
                                                    handleRemoveTag(tag)
                                                  }
                                                  className="h-3 w-3 p-0 hover:bg-transparent"
                                                >
                                                  <X className="h-2 w-2" />
                                                </Button>
                                              </Badge>
                                            )
                                          )}
                                        </div>

                                        {/* Add new tag input */}
                                        <div className="flex gap-2">
                                          <Input
                                            value={newTag}
                                            onChange={(e) =>
                                              setNewTag(e.target.value)
                                            }
                                            onKeyPress={handleTagKeyPress}
                                            placeholder="Add new tag"
                                            className="flex-1"
                                          />
                                          <Button
                                            size="sm"
                                            onClick={handleAddTag}
                                            disabled={!newTag.trim()}
                                            className="px-3"
                                          >
                                            <Plus className="h-4 w-4" />
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="flex gap-2 pt-4">
                                    <Button
                                      onClick={() =>
                                        handleSaveEdit(item.originalIndex)
                                      }
                                      size="sm"
                                    >
                                      Save Changes
                                    </Button>
                                    <Button
                                      onClick={handleCancelEdit}
                                      variant="outline"
                                      size="sm"
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      )}
                    </div>
                  );
                })}
              </Accordion>
            </div>
            {/* Footer */}
            <div className="p-4 border-t bg-gray-50">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  {hasValidQuestionsToSubmit
                    ? `Ready to upload ${stats.valid} valid questions`
                    : !questionBankCapacity.hasSpace
                      ? "Capacity exceeded - cannot upload"
                      : "No valid questions to upload"}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    disabled={isProcessing}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={!hasValidQuestionsToSubmit || isProcessing}
                    className="min-w-32"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Processing...
                      </>
                    ) : (
                      `Upload ${stats.valid} Questions`
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Error View Modal */}
      <ErrorViewModal 
        open={errorViewOpen} 
        onOpenChange={setErrorViewOpen} 
        item={selectedErrorItem} 
      />
    </>
  );
};

export default ExcelValidationModal;
